import { get } from 'lodash'
import { FormDataConsumer, required, TextInput, UPDATE, useRecordContext } from 'react-admin'

import { AdvancedTimeInput } from '../../components/AdvancedDateTimeInputs'
import AdvancedNumberInput from '../../components/AdvancedNumberInput'
import AdvancedSelectInput from '../../components/AdvancedSelectInput'
import BasicFormToolbar from '../../components/BasicFormToolbar'
import BillingTypesRadioButtonGroupInput from '../../components/BillingTypesRadioButtonGroupInput'
import DefaultBillingTypeSelectInput from '../../components/DefaultBillingTypeSelectInput'
import LabelWithHelp from '../../components/LabelWithHelp'
import {
  ACCOUNT_BILLING_TYPES_SOURCE,
  ACCOUNT_DISPLAY_ESTIMATED_PRICE_IN_EMAIL_SOURCE,
  ACCOUNT_FREE_BILLING_TYPE_REQUIRES_JUSTIFICATION_SOURCE,
} from '../../config/accounts'
import { BOOKING_BILLING_TYPE_FREE } from '../../config/bookings'
import { canDriversPayForBookings } from '../../domain/accounts'
import { hasBillingType, hasMobilityBudgetBillingType } from '../../utils'
import { validateEndTimeAfterStartTime } from '../../utils/dates'
import { FormDivider } from '../common'
import { AdvancedSimpleForm } from '../common/forms'

const MINUTES_PER_DAY = 24 * 60
const BOOKING_MAXIMUM_DAYS = 365

const isBlank = (value) => value === '' || value == null

const bookingMaximumDaysToMinutes = (days) => (isBlank(days) || Number(days) === 0 ? null : days * MINUTES_PER_DAY)

const bookingMaximumMinutesToDays = (minutes) => (isBlank(minutes) ? '' : minutes / MINUTES_PER_DAY)

const validateBookingMaximumDuration = (minutes) =>
  minutes == null || (minutes >= MINUTES_PER_DAY && minutes <= BOOKING_MAXIMUM_DAYS * MINUTES_PER_DAY)
    ? undefined
    : 'resources.accounts.forms.validation.booking_maximum_duration'

export const transformValues = ({
  booking_default_duration,
  display_estimated_price_in_email,
  free_billing_type_requires_justification,
  has_mobility_budget,
  ...restValues
}) => ({
  ...restValues,
  [ACCOUNT_FREE_BILLING_TYPE_REQUIRES_JUSTIFICATION_SOURCE]: hasBillingType(
    restValues[ACCOUNT_BILLING_TYPES_SOURCE],
    BOOKING_BILLING_TYPE_FREE,
  )
    ? free_billing_type_requires_justification
    : false,
  [ACCOUNT_DISPLAY_ESTIMATED_PRICE_IN_EMAIL_SOURCE]:
    canDriversPayForBookings(restValues[ACCOUNT_BILLING_TYPES_SOURCE]) && Boolean(display_estimated_price_in_email),
  ...(!restValues.display_step_duration_slider && { booking_default_duration }),
})

const AccountFormLayout = ({ onSubmit }) => {
  const record = useRecordContext()
  const initialBillingTypes = get(record, ACCOUNT_BILLING_TYPES_SOURCE, [])
  return (
    <AdvancedSimpleForm
      defaultValues={{
        has_mobility_budget: hasMobilityBudgetBillingType(initialBillingTypes),
        pro_hours_start: '09:00:00',
        pro_hours_end: '18:00:00',
      }}
      onSubmit={onSubmit}
      record={record}
      toolbar={<BasicFormToolbar formType={UPDATE} />}
      type={UPDATE}
    >
      <TextInput source="name" validate={required()} />
      <FormDivider />
      <AdvancedSelectInput source="has_mobility_budget" />
      <AdvancedSelectInput source="requires_payment" />
      <FormDivider />
      <BillingTypesRadioButtonGroupInput />
      <DefaultBillingTypeSelectInput />
      <AdvancedNumberInput source="free_cancellation_period_hours" min={0} step={1} unit="h" />
      <AdvancedNumberInput
        source="minimum_booking_charge_percent"
        validate={required()}
        min={0}
        step={1}
        max={100}
        unit="%"
      />
      <FormDataConsumer>
        {({ formData }) =>
          canDriversPayForBookings(formData[ACCOUNT_BILLING_TYPES_SOURCE]) && (
            <AdvancedSelectInput
              source={ACCOUNT_DISPLAY_ESTIMATED_PRICE_IN_EMAIL_SOURCE}
              label={
                <LabelWithHelp
                  label="resources.accounts.fields.display_estimated_price_in_email.short"
                  help="resources.accounts.forms.helpTooltips.display_estimated_price_in_email"
                />
              }
            />
          )
        }
      </FormDataConsumer>
      <FormDataConsumer>
        {({ formData }) =>
          hasBillingType(formData[ACCOUNT_BILLING_TYPES_SOURCE], BOOKING_BILLING_TYPE_FREE) && (
            <>
              <AdvancedSelectInput source={ACCOUNT_FREE_BILLING_TYPE_REQUIRES_JUSTIFICATION_SOURCE} />
              <AdvancedSelectInput
                source="end_forgotten_bookings_after_1_hour"
                label="resources.accounts.fields.end_forgotten_bookings_after_1_hour.short"
              />
            </>
          )
        }
      </FormDataConsumer>
      <FormDataConsumer>
        {({ formData }) =>
          formData.has_mobility_budget && (
            <>
              <FormDivider />
              <AdvancedTimeInput source="pro_hours_start" validate={required()} />
              <AdvancedTimeInput
                validate={[required(), (value) => validateEndTimeAfterStartTime(formData.pro_hours_start, value)]}
                source="pro_hours_end"
              />
            </>
          )
        }
      </FormDataConsumer>
      <FormDivider />
      <AdvancedSelectInput source="is_free_to_join" />
      <AdvancedSelectInput source="display_step_duration_slider" />
      <FormDataConsumer>
        {({ formData }) =>
          !formData.display_step_duration_slider && (
            <AdvancedNumberInput
              source="booking_default_duration"
              validate={required()}
              parse={(v) => (v ? v * 60 : null)}
              format={(v) => (v ? v / 60 : null)}
              min={1}
              max={24}
              unit="mymove.units.time.hours"
            />
          )
        }
      </FormDataConsumer>
      <AdvancedNumberInput
        source="booking_maximum_duration"
        validate={validateBookingMaximumDuration}
        parse={bookingMaximumDaysToMinutes}
        format={bookingMaximumMinutesToDays}
        unit="mymove.units.time.days"
      />
      <FormDivider />
      <TextInput source="mobile_legal_url" type="url" validate={required()} />
    </AdvancedSimpleForm>
  )
}

export default AccountFormLayout
